type Detail = {
  promise: string
  intro: string
  included: string[]
  optionsTitle: string
  options: { name: string; detail: string }[]
  faq: number[]
}

type Details = {
  labels: {
    back: string
    from: string
    quote: string
    included: string
    examples: string
    seeAll: string
    faqTitle: string
    palettesTitle: string
    palettes: { pastel: string; vibrant: string; neutral: string }
    ctaTitle: string
    ctaText: string
    whatsappPrefix: string
  }
  pages: Record<string, Detail>
}

const en: Details = {
  labels: {
    back: 'All services',
    from: 'Starting at',
    quote: 'Request a Quote',
    included: 'What’s included',
    examples: 'Recent examples',
    seeAll: 'See more in the portfolio',
    faqTitle: 'Common questions',
    palettesTitle: 'Popular balloon palettes',
    palettes: { pastel: 'Pastel dream', vibrant: 'Vibrant party', neutral: 'Champagne & gold' },
    ctaTitle: 'Let’s design yours',
    ctaText: 'Share your date, venue and colors on WhatsApp — we reply with ideas and a fixed price.',
    whatsappPrefix: 'Hi! I’d like a quote for:',
  },
  pages: {
    'wedding-flowers': {
      promise: 'Florals that feel like you — from the first look to the last dance.',
      intro:
        'We design every wedding around your palette, venue and budget, then build and install everything on the day so you never have to think about it.',
      included: [
        'Bridal bouquet and bridesmaid bouquets',
        'Boutonnieres and corsages',
        'Ceremony arch or backdrop florals',
        'Aisle petals and chair markers',
        'Sweetheart and reception table florals',
      ],
      optionsTitle: 'Customization options',
      options: [
        { name: 'Intimate', detail: 'Bouquets, boutonnieres and a sweetheart table — ideal for up to 50 guests.' },
        { name: 'Classic', detail: 'Adds ceremony arch florals and guest table centerpieces.' },
        { name: 'Signature', detail: 'Full venue styling with installations, draping and floral pedestals.' },
      ],
      faq: [1, 7, 5],
    },
    'balloon-arches': {
      promise: 'Statement arches and garlands that turn any room into a celebration.',
      intro:
        'Organic garlands, classic arches and full backdrop installations built on site in the colors of your event — with flowers woven in when you want extra elegance.',
      included: [
        'Design consultation and color selection',
        'Organic garland or structured arch',
        'Delivery, on-site assembly and takedown',
        'Optional floral accents and signage',
      ],
      optionsTitle: 'Arch styles & sizes',
      options: [
        { name: 'Garland (6–8 ft)', detail: 'Perfect for dessert tables and entryways.' },
        { name: 'Full arch (10–14 ft)', detail: 'Frames a stage, backdrop or photo area.' },
        { name: 'Backdrop installation', detail: 'Panels, arches and garlands combined into a complete scene.' },
      ],
      faq: [3, 2, 6],
    },
    centerpieces: {
      promise: 'Tables your guests will photograph before they sit down.',
      intro:
        'Low lush arrangements, tall statement pieces, candle-lit garlands and hanging installations — styled to your venue and delivered ready to place.',
      included: [
        'Guest table centerpieces',
        'Head or sweetheart table styling',
        'Candles, holders and runners on request',
        'On-site placement and setup',
      ],
      optionsTitle: 'Pricing tiers by guest count',
      options: [
        { name: 'Up to 50 guests', detail: '5–6 tables · from $85 per table' },
        { name: '50–120 guests', detail: '7–15 tables · volume pricing available' },
        { name: '120+ guests', detail: 'Full-room plan with installations · custom quote' },
      ],
      faq: [7, 0, 1],
    },
    'themed-balloons': {
      promise: 'Your theme, brought to life in balloons — down to the last detail.',
      intro:
        'From storybook first birthdays to charro celebrations and baby showers, we design themed setups with custom colors, character cutouts, backdrops and props.',
      included: [
        'Theme and color concept',
        'Balloon garlands and columns',
        'Character cutouts and custom signage',
        'Backdrop panels and props',
        'Setup and takedown',
      ],
      optionsTitle: 'Popular themes',
      options: [
        { name: 'Storybook & character', detail: 'Pastel garlands, daisy balloons and character cutouts.' },
        { name: 'Mi primer año', detail: 'Charro, fiesta and cultural themes in cream, gold and terracotta.' },
        { name: 'Baby shower', detail: 'Soft pastels, neutral tones and personalized signs.' },
      ],
      faq: [4, 3, 6],
    },
    'custom-arrangements': {
      promise: 'A bouquet made just for them — any day, any reason.',
      intro:
        'Hand-tied bouquets and vase arrangements built with seasonal blooms for birthdays, anniversaries, sympathy and just-because moments.',
      included: [
        'Seasonal, fresh-cut flowers',
        'Hand-tied wrap or vase of your choice',
        'Personalized card',
        'Local delivery available',
      ],
      optionsTitle: 'Styles & price points',
      options: [
        { name: 'Hand-tied bouquet', detail: 'Wrapped and ready to gift · from $65' },
        { name: 'Vase arrangement', detail: 'Arranged in glass or ceramic · from $85' },
        { name: 'Luxury arrangement', detail: 'Premium roses, orchids and peonies · from $150' },
      ],
      faq: [6, 7, 0],
    },
    'celebration-combinations': {
      promise: 'Flowers and balloons together — the look everyone remembers.',
      intro:
        'Our most-requested package for quinceañeras, baby showers and corporate events: balloon arches and backdrops paired with floral centerpieces and accents.',
      included: [
        'Balloon arch or backdrop installation',
        'Floral centerpieces for guest tables',
        'Main table or throne styling',
        'Coordinated color palette across both',
        'Full setup on site',
      ],
      optionsTitle: 'Great for',
      options: [
        { name: 'Quinceañeras', detail: 'Pink & gold arches, crowns, floral centerpieces.' },
        { name: 'Baby showers', detail: 'Pastel balloons with soft floral accents.' },
        { name: 'Corporate events', detail: 'Brand-color balloons with elegant florals.' },
      ],
      faq: [2, 5, 1],
    },
  },
}

const es: Details = {
  labels: {
    back: 'Todos los servicios',
    from: 'Desde',
    quote: 'Solicitar Cotización',
    included: 'Qué incluye',
    examples: 'Ejemplos recientes',
    seeAll: 'Ver más en el portafolio',
    faqTitle: 'Preguntas comunes',
    palettesTitle: 'Paletas de globos populares',
    palettes: { pastel: 'Sueño pastel', vibrant: 'Fiesta vibrante', neutral: 'Champaña y oro' },
    ctaTitle: 'Diseñemos el suyo',
    ctaText: 'Comparta su fecha, lugar y colores por WhatsApp — le respondemos con ideas y un precio fijo.',
    whatsappPrefix: '¡Hola! Me gustaría una cotización para:',
  },
  pages: {
    'wedding-flowers': {
      promise: 'Flores que se sienten como ustedes — del primer vistazo al último baile.',
      intro:
        'Diseñamos cada boda según su paleta, lugar y presupuesto, y lo instalamos todo el mismo día para que no tenga que preocuparse.',
      included: [
        'Ramo de novia y ramos de damas',
        'Boutonnieres y corsages',
        'Flores para el arco o fondo de ceremonia',
        'Pétalos de pasillo y decoración de sillas',
        'Flores para la mesa de novios y la recepción',
      ],
      optionsTitle: 'Opciones de personalización',
      options: [
        { name: 'Íntima', detail: 'Ramos, boutonnieres y mesa de novios — ideal hasta 50 invitados.' },
        { name: 'Clásica', detail: 'Agrega flores para el arco y centros de mesa para invitados.' },
        { name: 'Signature', detail: 'Decoración completa con instalaciones, telas y pedestales florales.' },
      ],
      faq: [1, 7, 5],
    },
    'balloon-arches': {
      promise: 'Arcos y guirnaldas que convierten cualquier salón en una fiesta.',
      intro:
        'Guirnaldas orgánicas, arcos clásicos e instalaciones completas armadas en el lugar con los colores de su evento — con flores integradas si desea más elegancia.',
      included: [
        'Consulta de diseño y selección de colores',
        'Guirnalda orgánica o arco estructurado',
        'Entrega, armado en el lugar y retiro',
        'Detalles florales y letreros opcionales',
      ],
      optionsTitle: 'Estilos y tamaños de arco',
      options: [
        { name: 'Guirnalda (6–8 pies)', detail: 'Perfecta para mesas de postres y entradas.' },
        { name: 'Arco completo (10–14 pies)', detail: 'Enmarca un escenario, fondo o área de fotos.' },
        { name: 'Instalación de fondo', detail: 'Paneles, arcos y guirnaldas en una escena completa.' },
      ],
      faq: [3, 2, 6],
    },
    centerpieces: {
      promise: 'Mesas que sus invitados fotografiarán antes de sentarse.',
      intro:
        'Arreglos bajos y frondosos, piezas altas, guirnaldas con velas e instalaciones colgantes — adaptados a su lugar y entregados listos para colocar.',
      included: [
        'Centros de mesa para invitados',
        'Decoración de mesa principal o de novios',
        'Velas, candelabros y caminos de mesa a pedido',
        'Colocación e instalación en el lugar',
      ],
      optionsTitle: 'Precios por número de invitados',
      options: [
        { name: 'Hasta 50 invitados', detail: '5–6 mesas · desde $85 por mesa' },
        { name: '50–120 invitados', detail: '7–15 mesas · precios por volumen' },
        { name: 'Más de 120 invitados', detail: 'Plan completo con instalaciones · cotización personalizada' },
      ],
      faq: [7, 0, 1],
    },
    'themed-balloons': {
      promise: 'Su tema hecho realidad en globos — hasta el último detalle.',
      intro:
        'Desde primeros cumpleaños de cuento hasta celebraciones charras y baby showers, diseñamos montajes temáticos con colores, figuras de personajes, fondos y accesorios.',
      included: [
        'Concepto de tema y colores',
        'Guirnaldas y columnas de globos',
        'Figuras de personajes y letreros personalizados',
        'Paneles de fondo y accesorios',
        'Instalación y retiro',
      ],
      optionsTitle: 'Temas populares',
      options: [
        { name: 'Cuento y personajes', detail: 'Guirnaldas pastel, globos de margarita y figuras.' },
        { name: 'Mi primer año', detail: 'Temas charros, de fiesta y culturales en crema, oro y terracota.' },
        { name: 'Baby shower', detail: 'Pasteles suaves, tonos neutros y letreros personalizados.' },
      ],
      faq: [4, 3, 6],
    },
    'custom-arrangements': {
      promise: 'Un ramo hecho solo para esa persona — cualquier día, por cualquier motivo.',
      intro:
        'Ramos hechos a mano y arreglos en florero con flores de temporada para cumpleaños, aniversarios, condolencias y sorpresas.',
      included: [
        'Flores frescas de temporada',
        'Envoltura o florero a su elección',
        'Tarjeta personalizada',
        'Entrega local disponible',
      ],
      optionsTitle: 'Estilos y precios',
      options: [
        { name: 'Ramo hecho a mano', detail: 'Envuelto y listo para regalar · desde $65' },
        { name: 'Arreglo en florero', detail: 'En vidrio o cerámica · desde $85' },
        { name: 'Arreglo de lujo', detail: 'Rosas, orquídeas y peonías premium · desde $150' },
      ],
      faq: [6, 7, 0],
    },
    'celebration-combinations': {
      promise: 'Flores y globos juntos — el estilo que todos recuerdan.',
      intro:
        'Nuestro paquete más solicitado para quinceañeras, baby showers y eventos corporativos: arcos y fondos de globos combinados con centros de mesa y detalles florales.',
      included: [
        'Instalación de arco o fondo de globos',
        'Centros de mesa florales para invitados',
        'Decoración de mesa principal o trono',
        'Paleta de colores coordinada',
        'Instalación completa en el lugar',
      ],
      optionsTitle: 'Ideal para',
      options: [
        { name: 'Quinceañeras', detail: 'Arcos rosa y oro, coronas y centros de mesa florales.' },
        { name: 'Baby showers', detail: 'Globos pastel con detalles florales suaves.' },
        { name: 'Eventos corporativos', detail: 'Globos con colores de marca y flores elegantes.' },
      ],
      faq: [2, 5, 1],
    },
  },
}

export const serviceDetails = { en, es }
